import { useMemo, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import { CircularProgress } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

// Ajce Hostel App React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Ajce Hostel App React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import { filterBySemBranch } from "framework/students/advanced_search";
import { useSearchParams } from "react-router-dom";

function SearchDataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  countPerFetch,
  loadingState,
  searchedFilter,
  lastDocSnapshotState,
  setResult,
  setSnackbar,
}) {
  const [MUIController] = useMaterialUIController();
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25"];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = loadingState;
  const [lastDocSnapshot, setLastDocSnapshot] = lastDocSnapshotState;

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    setPageSize,
    setGlobalFilter,
    state: { pageSize, globalFilter },
  } = tableInstance;

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPageSize(value);

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  async function handleLoad(count) {
    try {
      setIsLoading(true);
      const { result, lastDocSnapshot: lastDocSnap } = await filterBySemBranch(
        searchedFilter,
        count,
        lastDocSnapshot
      );
      setResult((prev) => {
        return [...prev, ...result];
      });
      setLastDocSnapshot(lastDocSnap);
      setSearchParams({
        sem: searchParams.get("sem"),
        branch: searchParams.get("branch"),
        count: Number.parseInt(searchParams.get("count") ?? countPerFetch) + result.length,
      });
    } catch (err) {
      console.error(err);
      const msg = "Something went wrong, failed to fetch. Try again.";
      setSnackbar({
        description: err.message ?? msg,
        type: "error",
        color: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {entriesPerPage || canSearch ? (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" py={1} px={3}>
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10));
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}

          {canSearch && (
            <MDBox width="12rem" ml="auto">
              <MDInput
                placeholder="Search..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(search);
                  onSearchChange(currentTarget.value);
                }}
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}
      <Table {...getTableProps()}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <DataTableHeadCell
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width ? column.width : "auto"}
                  align={column.align ? column.align : "left"}
                  sorted={setSortedValue(column)}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {rows.map((row, key) => {
            prepareRow(row);
            return (
              <TableRow {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <DataTableBodyCell
                    noBorder={noEndBorder && rows.length - 1 === key}
                    align={cell.column.align ? cell.column.align : "left"}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}
                  </DataTableBodyCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {table.rows.length === 0 && !isLoading && (
        <MDBox p={2} display="flex" justifyContent="center">
          <MDTypography variant="caption">No results.</MDTypography>
        </MDBox>
      )}

      {isLoading && (
        <MDBox p={2} display="flex" justifyContent="center">
          <CircularProgress color={MUIController.sidenavColor} />
        </MDBox>
      )}
      {searchedFilter && lastDocSnapshot && (
        <MDBox p={2} display="flex" justifyContent="center">
          <MDButton
            variant="gradient"
            circular
            color={MUIController.sidenavColor}
            sx={{ margin: "0 5px" }}
            disabled={isLoading}
            onClick={() => handleLoad(countPerFetch)}
          >
            Load More
          </MDButton>
          <MDButton
            variant="gradient"
            circular
            color={MUIController.sidenavColor}
            sx={{ margin: "0 5px" }}
            disabled={isLoading}
            onClick={() => handleLoad(null)}
          >
            Load All
          </MDButton>
        </MDBox>
      )}
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
SearchDataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
SearchDataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

export default SearchDataTable;
